import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, OnDestroy, inject, signal } from '@angular/core';
import { UserEffects } from '../../state/user/user.effects';
import { ICurrentUser } from '../../state/user/user.interface';
import { ScreenLockTokenService } from './screen-lock-token.service';
import { IScreenLockConfig, IScreenLockTarget, SCREEN_LOCK_DEFAULT_CONFIG } from './screen-lock.interface';

/**
 * Interaction events reset the idle countdown. Capture so overlays cannot swallow them.
 *
 * `scroll` is deliberately NOT here. A capture listener on `document` also sees element scrolls, and
 * `InfiniteAutoScrollDirective` moves `scrollTop` every ~100 ms on every auto-scrolling widget — which kept
 * the countdown permanently reset, so a board with auto-scroll never locked at all. `isTrusted` does not
 * help: a scroll event caused by a scripted `scrollTop` is still generated by the browser. Real user
 * scrolling always arrives with `wheel`, `touchmove`, `pointerdown` or `keydown`, so nothing is lost.
 */
const ACTIVITY_EVENTS: readonly string[] = [
  'mousemove',
  'mousedown',
  'pointerdown',
  'touchstart',
  'touchmove',
  'keydown',
  'wheel',
];

const TICK_INTERVAL_MS = 500;

/**
 * Idle detection + lock state for factory-floor screens. Started per screen via {@link start}/{@link stop},
 * never app-wide. The countdown runs from the last interaction, not from when the screen opened. The PIN is
 * never held here — {@link unlock} is driven by `ScreenLockStore`. See SCREEN_LOCK_CONTEXT.md.
 */
@Injectable({ providedIn: 'root' })
export class ScreenLockService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly tokenService = inject(ScreenLockTokenService);

  private readonly _config = signal<IScreenLockConfig>(SCREEN_LOCK_DEFAULT_CONFIG);
  private readonly _isKeypadOpen = signal<boolean>(false);
  private readonly _isLocked = signal<boolean>(false);
  private readonly _isRunning = signal<boolean>(false);
  private readonly _target = signal<IScreenLockTarget | null>(null);

  public readonly config = this._config.asReadonly();
  public readonly isKeypadOpen = this._isKeypadOpen.asReadonly();
  public readonly isLocked = this._isLocked.asReadonly();
  public readonly isRunning = this._isRunning.asReadonly();
  /** Which screen is guarded; the store needs it to build the verify request. */
  public readonly target = this._target.asReadonly();

  private lastActivityAt: number = Date.now();
  private tickIntervalId: ReturnType<typeof setInterval> | null = null;
  /**
   * Every document currently reporting activity: the host document plus the same-origin iframe documents a
   * slideshow embeds. Events do not cross a document boundary, so without the iframes a slideshow never sees
   * anyone using the board it is showing and locks underneath them. See SCREEN_LOCK_CONTEXT.md.
   */
  private activityDocuments: Document[] = [];

  /** Class field arrow so add/removeEventListener get the identical reference. */
  private readonly activityHandler = (): void => {
    this.lastActivityAt = Date.now();
  };

  public ngOnDestroy(): void {
    this.detachWatchers();
  }

  /**
   * Begins watching for inactivity. Idempotent — a re-resolving component can call it freely. A screen with a
   * lock configured opens locked, so a reload costs an unlock rather than granting one. See
   * SCREEN_LOCK_CONTEXT.md.
   */
  public start(target: IScreenLockTarget, config?: Partial<IScreenLockConfig>): void {
    if (ScreenLockService.isExemptFromLock()) {
      return;
    }

    this._target.set(target);

    if (config) {
      this.configure(config);
    }

    if (this._isRunning()) {
      return;
    }

    this._isRunning.set(true);
    this.registerActivity();
    this.lock();

    // runOutsideAngular: activity events fire constantly, avoid a change detection run per mouse move.
    this.ngZone.runOutsideAngular(() => {
      this.attachActivityListeners(this.document);
      this.syncIframeActivitySources();

      this.tickIntervalId = setInterval(() => this.tick(), TICK_INTERVAL_MS);
    });
  }

  /** Stops watching and unlocks. Call it when leaving a screen that had the lock running. */
  public stop(): void {
    const target: IScreenLockTarget | null = this._target();

    this.detachWatchers();
    this._isRunning.set(false);
    this._isKeypadOpen.set(false);
    this._isLocked.set(false);
    this._target.set(null);

    if (target) {
      this.tokenService.clear(target);
    }
  }

  /** Applies the entity's own idle timeout, as resolved by the API. */
  public configure(config: Partial<IScreenLockConfig>): void {
    this._config.update((current: IScreenLockConfig) => ({ ...current, ...config }));
    this.registerActivity();
  }

  public lock(): void {
    if (this._isLocked()) {
      return;
    }

    this._isKeypadOpen.set(false);
    this._isLocked.set(true);

    // The unlock must not outlive the unlocked state, so drop the token on re-lock.
    const target: IScreenLockTarget | null = this._target();

    if (target) {
      this.tokenService.clear(target);
    }
  }

  public openKeypad(): void {
    if (!this._isLocked()) {
      return;
    }

    this.registerActivity();
    this._isKeypadOpen.set(true);
  }

  public closeKeypad(): void {
    this.registerActivity();
    this._isKeypadOpen.set(false);
  }

  /** Called by `ScreenLockStore` once the API has confirmed the PIN. */
  public unlock(): void {
    this._isKeypadOpen.set(false);
    this._isLocked.set(false);
    this.registerActivity();
  }

  public registerActivity(): void {
    this.lastActivityAt = Date.now();
  }

  private static isExemptFromLock(): boolean {
    const user: ICurrentUser = UserEffects.getUser();

    return Boolean(user && user.isSuperAdmin);
  }

  private tick(): void {
    // Slideshow slides swap their iframe document on every change, so the sources are re-resolved per tick
    // rather than once at start().
    this.syncIframeActivitySources();

    const idleMs: number = Date.now() - this.lastActivityAt;
    const { idleTimeoutSeconds, keypadIdleTimeoutSeconds } = this._config();

    if (!this._isLocked()) {
      if (idleTimeoutSeconds > 0 && idleMs >= idleTimeoutSeconds * 1000) {
        this.ngZone.run(() => this.lock());
      }

      return;
    }

    if (this._isKeypadOpen() && keypadIdleTimeoutSeconds > 0 && idleMs >= keypadIdleTimeoutSeconds * 1000) {
      this.ngZone.run(() => this._isKeypadOpen.set(false));
    }
  }

  /**
   * Keeps the listener set in step with the same-origin iframes on screen. `contentDocument` is `null` for a
   * cross-origin frame (a `type: 'url'` slide), and touching a torn-down frame can throw, so both are treated
   * as "no activity reported from there" rather than as errors.
   */
  private syncIframeActivitySources(): void {
    const liveDocuments: Document[] = [this.document];

    this.document.querySelectorAll('iframe').forEach((iframe: HTMLIFrameElement) => {
      try {
        const iframeDocument: Document | null = iframe.contentDocument;

        if (iframeDocument?.body) {
          liveDocuments.push(iframeDocument);
        }
      } catch {
        // Cross-origin or torn down: nothing to listen to.
      }
    });

    this.activityDocuments
      .filter((attached: Document) => !liveDocuments.includes(attached))
      .forEach((stale: Document) => this.detachActivityListeners(stale));

    liveDocuments
      .filter((live: Document) => !this.activityDocuments.includes(live))
      .forEach((fresh: Document) => this.attachActivityListeners(fresh));
  }

  private attachActivityListeners(target: Document): void {
    if (this.activityDocuments.includes(target)) {
      return;
    }

    try {
      ACTIVITY_EVENTS.forEach((eventName: string) =>
        target.addEventListener(eventName, this.activityHandler, { capture: true, passive: true }),
      );
      this.activityDocuments.push(target);
    } catch {
      // A frame torn down between resolution and binding; it is simply not a source.
    }
  }

  private detachActivityListeners(target: Document): void {
    try {
      ACTIVITY_EVENTS.forEach((eventName: string) =>
        target.removeEventListener(eventName, this.activityHandler, { capture: true }),
      );
    } catch {
      // The document is already gone, so its listeners are too.
    }

    this.activityDocuments = this.activityDocuments.filter((attached: Document) => attached !== target);
  }

  private detachWatchers(): void {
    [...this.activityDocuments].forEach((attached: Document) => this.detachActivityListeners(attached));
    this.activityDocuments = [];

    if (this.tickIntervalId !== null) {
      clearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }
  }
}
